import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { AlertCircle } from "lucide-react";
import { parseAmount } from "./donationSchema";

/**
 * Single source of truth for the donation amount.
 *
 * Preset tiers and the custom field both resolve to ONE controlled value (the
 * react-hook-form `amount` field). A tier is "pressed" when the parsed numeric
 * value of the field equals the tier amount, so typing 5100 highlights the 5100
 * tier and clicking a tier fills the field — no desync, numeric (not string)
 * comparison.
 *
 * @param {object}   props
 * @param {Array<{amount:number,impactLine?:string,highlighted?:boolean}>} props.tiers
 * @param {string}   props.value         Current field value (RHF amount, a string).
 * @param {(amountString:string)=>void} props.onTierSelect
 * @param {string}   props.amountPrefix  Currency symbol ("$" / "₹").
 * @param {string}   props.currency      Currency code ("USD" / "INR").
 * @param {number}   props.min           Region minimum (drives the native min attr).
 * @param {string}   props.step          Native step attr ("1" / "0.01").
 * @param {object}   props.registration  RHF register("amount") output for the custom input.
 * @param {string=}  props.error         Amount error message.
 * @param {string}   props.inputId       Id for the custom amount input.
 */
export default function AmountSelector({
  tiers,
  value,
  onTierSelect,
  amountPrefix,
  currency,
  min,
  step,
  registration,
  error,
  inputId,
}) {
  const { t } = useTranslation();
  const numericValue = parseAmount(value);
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <fieldset className="space-y-3">
      <legend className="sr-only">{t("donateForm.amountSelector.legend", { currency })}</legend>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {tiers.map((tier) => {
          const isSelected = numericValue === tier.amount;
          return (
            <button
              key={tier.amount}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onTierSelect(String(tier.amount))}
              className={`relative rounded-md border px-3 py-3 text-left text-sm transition ${
                isSelected
                  ? "border-[#9d1f1c] bg-[#fff5f2] text-[#9d1f1c]"
                  : "border-[#dfe8e6] text-[#33413f] hover:border-[#9d1f1c]"
              }`}
            >
              {tier.highlighted && (
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.14em] text-[#9d1f1c]">
                  {t("donateForm.amountSelector.mostImpact")}
                </span>
              )}
              <span className="block font-semibold">
                {amountPrefix}
                {tier.amount.toLocaleString()}
              </span>
              {tier.impactLine && (
                <span className="mt-0.5 block text-xs leading-4 text-[#52615f]">
                  {tier.impactLine}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        <label htmlFor={inputId} className="sr-only">
          {t("donateForm.amountSelector.otherAmountLabel", { currency })}
        </label>
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          min={min}
          step={step}
          autoComplete="off"
          placeholder={t("donateForm.amountSelector.otherAmountPlaceholder", { currency })}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={errorId}
          aria-required="true"
          className={`w-full rounded-md border bg-white px-4 py-3 focus:border-[#9d1f1c] focus:ring-[#9d1f1c]/20 ${
            error ? "border-red-500" : "border-[#dfe8e6]"
          }`}
          {...registration}
        />
        {error && (
          <p id={errorId} className="mt-2 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle size={15} aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    </fieldset>
  );
}

AmountSelector.propTypes = {
  tiers: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      impactLine: PropTypes.string,
      highlighted: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.string,
  onTierSelect: PropTypes.func.isRequired,
  amountPrefix: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  step: PropTypes.string.isRequired,
  registration: PropTypes.object,
  error: PropTypes.string,
  inputId: PropTypes.string.isRequired,
};
